import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';
import { TESTING_MODE, MOCK_USER, MOCK_FAMILY, MOCK_MEMBERS } from '@/lib/testing-mode';
import { getEffectiveBirthDate } from '@/lib/birthday-utils';

interface FamilyMember {
  id: string;
  family_id: string;
  name: string;
  avatar_url: string | null;
  role: 'parent' | 'kid';
  points_balance: number;
  has_pin?: boolean;
  color?: string;
  calm_mode?: boolean;
  user_id?: string | null;
  avatar_emoji?: string | null;
  avatar_bg_color?: string | null;
  header_color?: string | null;
  gender?: 'boy' | 'girl' | 'unspecified' | null;
  birth_date?: string | null;
  birthday?: string | null;
}

interface Family {
  id: string;
  name: string;
  created_by: string;
  subscription_tier?: string;
  subscription_expires_at?: string | null;
  timezone?: string | null;
}

interface FamilyContextType {
  user: User | null;
  family: Family | null;
  members: FamilyMember[];
  activeKidId: string | null;
  setActiveKidId: (id: string | null) => void;
  loading: boolean;
  refreshData: () => Promise<void>;
  pricingEnabled: boolean;
  /** True when an authenticated parent could not load any family record. */
  dataAccessError: boolean;
}

const FamilyContext = createContext<FamilyContextType>({
  user: null,
  family: null,
  members: [],
  activeKidId: null,
  setActiveKidId: () => {},
  loading: true,
  refreshData: async () => {},
  pricingEnabled: false,
  dataAccessError: false,
});

const FAMILY_SAFE_COLUMNS = 'id,name,created_by,created_at,updated_at,country_code,timezone,message_of_day,show_public_holidays,subscription_tier,subscription_status,subscription_expires_at,trial_ends_at,rule_book,ai_last_insight,ai_last_insight_at' as const;

export const useFamilyContext = () => useContext(FamilyContext);

export const FamilyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(TESTING_MODE ? MOCK_USER : null);
  const [family, setFamily] = useState<Family | null>(TESTING_MODE ? MOCK_FAMILY : null);
  const [members, setMembers] = useState<FamilyMember[]>(TESTING_MODE ? MOCK_MEMBERS as FamilyMember[] : []);
  const [activeKidId, setActiveKidId] = useState<string | null>(null);
  const [loading, setLoading] = useState(TESTING_MODE ? false : true);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [dataAccessError, setDataAccessError] = useState(false);
  const [healthAlertSent, setHealthAlertSent] = useState(false);

  // Fetch global pricing setting
  useEffect(() => {
    const fetchPricing = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('pricing_enabled')
        .eq('id', 1)
        .maybeSingle();
      if (data) setPricingEnabled(data.pricing_enabled);
    };
    fetchPricing();
  }, []);

  const fetchFamilyData = useCallback(async () => {
    if (TESTING_MODE) { setLoading(false); return; }
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      setUser(null);
      setFamily(null);
      setMembers([]);
      setDataAccessError(false);
      setLoading(false);
      return;
    }
    setUser(currentUser);

    // First try: membership lookup (works for both creators and co-parents)
    let familyData: Family | null = null;
    let isParent = false;
    let lastError: string | null = null;

    const { data: memberData, error: memberErr } = await supabase
      .from('family_members')
      .select('family_id, role')
      .eq('user_id', currentUser.id)
      .limit(1)
      .maybeSingle();

    if (memberErr) lastError = `family_members lookup: ${memberErr.message}`;
    if (memberData?.role === 'parent') isParent = true;

    if (memberData?.family_id) {
      const { data, error } = await supabase
        .from('families')
        .select(FAMILY_SAFE_COLUMNS)
        .eq('id', memberData.family_id)
        .single();
      if (error) lastError = `families by member.family_id: ${error.message}`;
      familyData = data as Family | null;
    }

    // Fallback: legacy check by created_by
    if (!familyData) {
      const { data, error } = await supabase
        .from('families')
        .select(FAMILY_SAFE_COLUMNS)
        .eq('created_by', currentUser.id)
        .limit(1)
        .maybeSingle();
      if (error) lastError = `families by created_by: ${error.message}`;
      if (data) {
        familyData = data as Family | null;
        isParent = true;
      }
    }

    if (familyData) {
      setFamily(familyData);
      setDataAccessError(false);

      const { data: membersData } = await supabase
        .from('family_members')
        .select('*')
        .eq('family_id', familyData.id);

      if (membersData) {
        setMembers((membersData as FamilyMember[]).map((member) => ({
          ...member,
          birth_date: getEffectiveBirthDate(member.birth_date, member.birthday),
        })));
      }
    } else if (isParent) {
      // Authenticated user has parent role but family query returned null.
      // Surface the fallback panel and notify admins (once per session).
      setDataAccessError(true);
      if (!healthAlertSent) {
        setHealthAlertSent(true);
        try {
          await supabase.functions.invoke('send-admin-alert', {
            body: {
              subject: 'Parent cannot access their family data',
              body: `User ${currentUser.id} (${currentUser.email ?? 'unknown'}) has parent role but family query returned null. Possible RLS or permission issue.`,
              severity: 'error',
              category: 'security_event',
              alert_kind: 'failure',
              source: 'family-context-load',
              context: {
                user_id: currentUser.id,
                email: currentUser.email,
                role: 'parent',
                attempted_query: 'family_members(role=parent) → families.id, fallback families.created_by',
                error_message: lastError,
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
              },
            },
          });
        } catch (e) {
          console.error('[FamilyContext] failed to fire data-access admin alert', e);
        }
      }
    } else {
      setDataAccessError(false);
    }
    setLoading(false);
  }, [healthAlertSent]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        setTimeout(fetchFamilyData, 0);
      } else {
        setUser(null);
        setFamily(null);
        setMembers([]);
        setLoading(false);
      }
    });

    fetchFamilyData();

    return () => subscription.unsubscribe();
  }, [fetchFamilyData]);

  // Realtime subscription for family_members
  useEffect(() => {
    if (!family?.id) return;

    const channel = supabase
      .channel('family-members-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'family_members',
        filter: `family_id=eq.${family.id}`,
      }, () => {
        fetchFamilyData();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [family?.id, fetchFamilyData]);

  return (
    <FamilyContext.Provider value={{
      user,
      family,
      members,
      activeKidId,
      setActiveKidId,
      loading,
      refreshData: fetchFamilyData,
      pricingEnabled,
      dataAccessError,
    }}>
      {children}
    </FamilyContext.Provider>
  );
};
